// Mitgliedsnummer — fortlaufende, einmalige Nummer pro Konto (für die MemberCard).
// Spiegelt das sichere Muster von meta/betaCounter (firestore.rules):
//   - meta/memberCounter { count } wird transaktional um +1 erhöht
//   - memberNumbers/{uid} { no } hält die einmal vergebene Nummer fest
// Die Transaktion garantiert, dass keine zwei Konten dieselbe Nummer ziehen.
// Einmal vergeben, ändert sich die Nummer nie wieder.
//
// VORAUSSETZUNG (einmalig, Owner): meta/memberCounter muss mit { count: <start> }
// geseedet sein (scripts/seed-member-counter.mjs) und die Rules deployed sein.
// Ist der Zähler nicht geseedet, liefert getOrAssignMemberNumber null → die Karte
// zeigt einen Platzhalter statt zu crashen.
import { doc, getDoc, runTransaction, serverTimestamp } from '../lib/fsCompat';
import { db } from '../firebase';

const _cache = {}; // uid -> number (Session-Cache, spart Reads)

/**
 * Liest die Mitgliedsnummer des Users oder vergibt sie einmalig.
 * @returns {Promise<number|null>} die Nummer, oder null wenn (noch) nicht vergebbar.
 */
export async function getOrAssignMemberNumber(uid) {
  if (!uid) return null;
  if (_cache[uid] != null) return _cache[uid];

  const markerRef = doc(db, 'memberNumbers', uid);
  try {
    const existing = await getDoc(markerRef);
    if (existing.exists() && typeof existing.data().no === 'number') {
      _cache[uid] = existing.data().no;
      return _cache[uid];
    }
  } catch {
    return null;
  }

  const counterRef = doc(db, 'meta', 'memberCounter');
  try {
    const assigned = await runTransaction(db, async (tx) => {
      // Re-Check innerhalb der Transaktion (Race-Schutz).
      const mSnap = await tx.get(markerRef);
      if (mSnap.exists() && typeof mSnap.data().no === 'number') return mSnap.data().no;

      const cSnap = await tx.get(counterRef);
      if (!cSnap.exists()) throw new Error('member-counter-unseeded');
      const next = (cSnap.data().count || 0) + 1;
      tx.update(counterRef, { count: next, updatedAt: serverTimestamp() });
      tx.set(markerRef, { no: next, createdAt: serverTimestamp() });
      return next;
    });
    _cache[uid] = assigned;
    return assigned;
  } catch {
    // Zähler nicht geseedet, Rules nicht deployed oder Contention → Platzhalter.
    return null;
  }
}

/** 4-stellig formatiert (0007), wie im Karten-Mock. */
export function formatMemberNo(no) {
  if (no == null) return '——';
  return String(no).padStart(4, '0');
}
